<!--
  ChartLegend — the swatch key that sits beside DonutChart or BarChart,
  reading a category's colour, its already-formatted figure, and its share
  of the whole.

  Colour comes from an item's position in the `items` prop, exactly the way
  DonutChart picks a slice's colour from its position in `slices` — as long
  as a screen hands both components the same ordered `by_category` list, the
  Nth item here is the Nth ramp colour there too, so a category's colour
  never disagrees between the chart and its legend.

  Clicking an item calls the same `onselect` the donut's slices call, with
  the same clear-on-repeat-click behaviour, so the legend is a second way to
  reach one selection, not a second selection.
-->
<script lang="ts">
  interface Item {
    key: string;
    label: string;
    value: number;
    /** Already formatted by the caller — this component never rounds. */
    display: string;
  }

  interface Props {
    items: readonly Item[];
    selected?: string | null;
    onselect?: (key: string | null) => void;
  }

  const { items, selected = null, onselect }: Props = $props();

  const RAMP_SIZE = 8;

  // Same narrow exception as DonutChart's hidden table: a whole-number share
  // is not a money figure, so rounding it here does not risk the
  // "$1,234.5 vs $1,234.50" mismatch the no-formatting rule guards against.
  const total = $derived(
    items.reduce((sum, item) => (Number.isFinite(item.value) && item.value > 0 ? sum + item.value : sum), 0)
  );

  function share(value: number): string {
    if (!(total > 0) || !Number.isFinite(value) || value <= 0) return '—';
    return `${Math.round((value / total) * 100)}%`;
  }

  function toggle(key: string) {
    onselect?.(selected === key ? null : key);
  }
</script>

{#if items.length === 0}
  <p class="chart-legend-empty">Nothing to list for this period.</p>
{:else}
  <ul class="chart-legend">
    {#each items as item, i (item.key)}
      {@const isSelected = selected === item.key}
      <li>
        <button
          type="button"
          class="chart-legend__item"
          class:chart-legend__item--selected={isSelected}
          aria-pressed={isSelected}
          onclick={() => toggle(item.key)}
        >
          <span class="chart-swatch" style:background="var(--chart-{(i % RAMP_SIZE) + 1})" aria-hidden="true"
          ></span>
          <span class="chart-legend__label">{item.label}</span>
          <span class="chart-legend__display a-num">{item.display}</span>
          <span class="chart-legend__share a-num">{share(item.value)}</span>
        </button>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .chart-legend {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.3rem;
  }

  .chart-legend-empty {
    margin: 0;
    padding: 0.6rem 0.55rem;
    color: var(--ink-soft);
  }

  .chart-legend__item {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    min-height: var(--tap);
    padding: 0.2rem 0.55rem;
    border: 1.5px solid transparent;
    border-radius: var(--radius);
    background: transparent;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    touch-action: manipulation;
    transition: background 150ms ease-out, border-color 150ms ease-out;
  }
  .chart-legend__item:hover {
    background: var(--bg-sunken);
  }
  .chart-legend__item--selected {
    background: var(--brand-tint);
    border-color: var(--brand);
  }
  .chart-legend__item:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  .chart-legend__label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.86rem;
    font-weight: 600;
  }
  .chart-legend__display {
    flex: 0 0 auto;
    font-size: 0.86rem;
    font-weight: 700;
  }
  .chart-legend__share {
    flex: 0 0 auto;
    min-width: 2.6rem;
    text-align: right;
    font-size: 0.76rem;
    color: var(--ink-faint);
  }

  @media (prefers-reduced-motion: reduce) {
    .chart-legend__item { transition: none; }
  }
</style>
